import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import type { Shop, ShopConfiguration } from "@/shared/types/shop";
import type { ConfigurationState } from "./slice";

/** Fase 8: `state.configuration`. Oggi i campi vivono nello slice legacy (vedi slice.ts). */
const configuration = (state: RootState): ConfigurationState => state.legacy as unknown as ConfigurationState;

export const selectCurrentConfiguration = (state: RootState): ShopConfiguration =>
  configuration(state).currentConfiguration;
export const selectCurrentShopId = (state: RootState): Shop["id"] | undefined =>
  configuration(state).currentConfiguration?.id;
export const selectHistoryChanges = (state: RootState): ShopConfiguration[] =>
  configuration(state).historyChanges ?? [];
export const selectHistoryLength = (state: RootState): number => selectHistoryChanges(state).length;
export const selectWebsiteAttributes = (state: RootState): string[] => {
  const attributes = configuration(state).websiteAttributes;
  return Array.isArray(attributes) ? attributes : [];
};

export type SetupSection = NonNullable<ShopConfiguration["setup"]>;
export type SetupConfig = NonNullable<SetupSection["config"]>;
export interface FineTuningSection {
  nlq: string;
  ips: string[];
  stopwords: string[];
  synonyms: string[][];
  weights: Record<string, number>;
  [key: string]: unknown;
}

const EMPTY_SETUP: SetupSection = {};
const EMPTY_CONFIG: SetupConfig = {};
export const selectSetup = (state: RootState): SetupSection =>
  selectCurrentConfiguration(state)?.setup ?? EMPTY_SETUP;
export const selectSetupConfig = (state: RootState): SetupConfig => selectSetup(state).config ?? EMPTY_CONFIG;
export const selectFineTuning = createSelector([selectCurrentConfiguration], (current): FineTuningSection => {
  const tuning = (current?.fineTuning ?? {}) as Partial<FineTuningSection>;
  return {
    ...tuning,
    nlq: tuning.nlq ?? "off",
    ips: Array.isArray(tuning.ips) ? tuning.ips : [],
    stopwords: Array.isArray(tuning.stopwords) ? tuning.stopwords : [],
    synonyms: Array.isArray(tuning.synonyms) ? tuning.synonyms : [],
    weights: tuning.weights ?? {},
  };
});
